
import { Component, Mixins } from 'vue-property-decorator';
import Page from '@/views/Page.vue';
import { PaginatedTableMixin, VuetifyMixin, CoachRoutingMixin, BAIconsMixin, SportsSelectorMixin, AthletesCacheMixin } from '@/mixins';
import { adminStore } from '@/store';
import { DataTableHeader } from 'vuetify';
import VerifiedAssessmentImportV4 from '@/components/admin/VerifiedAssessmentImportV4.vue';
import VerifiedAssessmentImportV5 from '@/components/admin/VerifiedAssessmentImportV5.vue';
import { AthleteAssessmentDataModel, AthleteAssessmentDataModelEx, AthleteAssessmentDataModelExport, AthleteProfileModel, SportModel, SportLabel, BAPurchaseModel } from '@/models';
import { athleteAssessmentDataApi } from '@/api/AthleteAssessmentDataApi';
import { AssessmentEntry } from '@/../types/interfaces/PyhsicalAssessmentDataFormValue';
import VerifiedAssessmentImportStatus from '@/components/admin/VerifiedAssessmentImportStatus.vue';
import { TeamModel } from '@/models/team';
import { OrganizationModel } from '@/models/organization/OrganizationModel';
import { notificationStore } from '@/store/index';
import { AgeGroupValues } from '@best-athletes/ba-types';
import { SportName } from '@/../types';
import { AthleteMetric, AthleteMetricValues, Gender, Length, Mass } from '@/../types/enums';
import AthleteStatCard from '@/components/profile/athlete/AthleteStatCard.vue';
import { athleteApi } from '@/api/AthleteApi';
import { baPurchaseApi } from '@/api/BAPurchaseApi';
import convert from 'convert-units';

@Component({
	components: {
		Page,
    	VerifiedAssessmentImportStatus,
		VerifiedAssessmentImportV4,
		VerifiedAssessmentImportV5,
		AthleteStatCard,
	}
})
export default class VerifiedAssessmentAdminDashboard extends Mixins(PaginatedTableMixin, VuetifyMixin, CoachRoutingMixin, BAIconsMixin, AthletesCacheMixin, SportsSelectorMixin){
	jobIds: number[] = [];
	uploadStatus: number = 0;
	sport: SportName;
	currentSport: SportModel;
	expanded: [];

	Soccer = SportName.Soccer;

	async mounted() {
		if( this.IsNotEmpty(this.sport)) this.currentSport = await this.SportRetrieve(this.sport);
		this.tableOptions.itemsPerPage = 10;
		this.tableOptions.sortBy = ['assessmentDate','athlete.lastName'];
		this.tableOptions.sortDesc = [true,false];
		this.localForagePersistFields = [['search', ''],['tableOptions.page', 1],['tableOptions.itemsPerPage', 10],['sport', ''],['filterGender','']];
	}

	// Selected athlete to filter results for
	athlete: AthleteProfileModel;
	async onAthleteSelected(athlete: AthleteProfileModel) {
		this.athlete = this.IsNotEmpty(athlete)? new AthleteProfileModel().load(athlete) : undefined;
		if( this.IsNotEmpty(this.editAssessment) ) this.editAssessment.athlete = this.athlete;
		await this.updateTable();
	}
	async onFilterAthleteReset() {
		await  this.onAthleteSelected(undefined);
	}

  	// Selected team to filter results for
	team: TeamModel | null = null;
	updateTeam(team: TeamModel): void {
		this.team = team ?? null;
		this.updateSearch();
	}

	// Selected organization to filter results for
	organization: OrganizationModel | null = null;
	updateOrganization(organization: OrganizationModel): void {
		this.organization = organization ?? null;
		this.updateSearch();
	}

	// Selected age group to filter results for
	ageGroup: number | null = null;
	updateAgeGroup(ageGroup: number): void {
		this.ageGroup = ageGroup ?? null;
		this.updateSearch();
	}
	get AgeGroups(): Record<string, string>[] {
		return AgeGroupValues.map(ag => ({
			text: String(ag),
			value: String(ag),
		}))
	}

	// Filter by Gender
	filterGender: Gender;
	onUpdateFilterGender(gender: Gender) {
		this.filterGender = gender?? undefined;
		this.updateSearch();
	}
	get FilterGenders(): Array<Gender> {
		return [Gender.Male, Gender.Female];
	}

	updateUpload() {
		this.uploadStatus = 0;
		this.updateTable();
	}

	tableHeaders: Array<DataTableHeader<any>> = [];
	GenerateTableHeaders(): Array<DataTableHeader<any>> {
		let headers: Array<DataTableHeader<any>> = [
			{ text: 'Date', value: 'assessmentDate', sortable: false },
			{ text: 'Athlete', value: 'name', sortable: false },
			{ text: 'id', value: 'athleteId', sortable: false },
			{ text: 'G', value: 'gender', sortable: false },
			{ text: 'Age', value: 'playingLevel', sortable: false },
		];
		if( this.IsEmpty(this.sport) ) {
			headers.push(
				{ text: 'Sport', value: 'sport', sortable: false },
			)
		}
		if( !this.IsMobile ) {
			headers.push(
				{ text: 'Height', value: 'standingHeight', sortable: false },
				{ text: 'Weight', value: 'mass', sortable: false },
				{ text: 'DoB', value: 'dateOfBirth', sortable: false },
			)
		}
		if( this.sport === SportName.Soccer ) {
			headers.push(
				{ text: 'Acc', value: 'acceleration', sortable: false },
				{ text: 'Spd', value: 'speed', sortable: false },
				{ text: 'Agl', value: 'agility', sortable: false },
				{ text: 'Pow', value: 'power', sortable: false },
				{ text: 'Rec', value: 'recovery', sortable: false },
			)
		} else if( this.IsNotEmpty(this.currentSport) ) {
			this.currentSport.physicalAssessmentLabels.forEach(label =>
				headers.push( { text: label.abbreviation, value: `data.${label.label}`, sortable: false } )
			)
		}
		headers.push(
			{ text: '', value: 'actions', sortable: false },
			{ text:'', value: 'data-table-expand'},
		);
		return headers;
	}

	async loadTable(): Promise<void>{
		this.isLoaded = false;
		this.isLoading = true;
		try {
			var query = this.TableQuery<AthleteAssessmentDataModel>(this.search, ['athleteId', 'assessmentDate', 'tags', 'athlete.lastName', 'athlete.firstName']);
			if( !!this.athlete ) { query.$match = {...query.$match, ...{ athleteId: this.athlete.id, } } };
    		if( !!this.team ) { query.$match = {...query.$match, ...{ teamIds: this.team.id, } } };
			if( this.IsNotEmpty(this.organization) ) query.$match = { ...query.$match, ...{ 'teams.organizations.orgId': this.organization.id, } };
    		if( this.IsNotEmpty(this.ageGroup) ) query.$match = { ...query.$match, ...{ playingLevel: this.ageGroup, } };
			if( this.IsNotEmpty(this.sport) ) query.$match = { ...query.$match, ...{ sport: this.sport } };
			if( this.IsNotEmpty(this.filterGender) ) query.$match = { ...query.$match, ...{ gender: this.filterGender } };

			await adminStore.loadVerifiedAssessmentList({ query, options: this.TableQueryOptions });
			this.dataItemsCount = adminStore.totalVerifiedAssessments;

			const verifiedAssessments: Array<AthleteAssessmentDataModelEx> = await Promise.all( adminStore.verifiedAssessmentList.map( async(assessment: AthleteAssessmentDataModel) => {
				const verified: AthleteAssessmentDataModelEx = new AthleteAssessmentDataModelEx().load(assessment);
				verified.athlete = await this.AthleteRetrieve(assessment.athleteId);
				verified.sportModel = await this.SportRetrieve(assessment.sport);
				return verified;
			}))
			const verifiedAssessmentsData: Array<Record<string, any>> = verifiedAssessments.map(a => {
				if( this.sport === SportName.Soccer ) return a;
				return {...a, ...a.data};
			})
			this.dataItems = verifiedAssessmentsData.map(v => new AthleteAssessmentDataModelEx().load(v));
			this.tableHeaders = this.GenerateTableHeaders();
		} catch(e) {
			notificationStore.pushSnackbarError({message: `Error loading athletes: ${e}`});
		} finally {
			this.isLoaded = true;
			this.isLoading = false;
		}
	}
	get PageLoading(): boolean{
		return this.TableLoading || adminStore.verifiedAssessmentsLoading || adminStore.organizationsLoading;
	}

  	async bulkImportAssessmentsV4({assessments,notify,fixDuplicates}: { assessments: AssessmentEntry[], notify: boolean, fixDuplicates: boolean }): Promise<void> {
		this.uploadStatus = 1;
		const {jobIds} = await athleteAssessmentDataApi.bulkInsertV4({assessments,notify,fixDuplicates});
    	this.jobIds = jobIds;
	}

	viewData = "view-measured";
	get isViewMeasured(): boolean {
		return this.viewData == "view-measured";
	}
	get isViewComputed(): boolean {
		return this.viewData == "view-computed";
	}

    async onDelete(assessment: AthleteAssessmentDataModel) {
        if( !confirm(`Delete assessment? This action cannot be undone`) ) return;

        await adminStore.deleteVerifiedAssessment(assessment);
        await this.updateTable();
    }

	flattenObject(obj: Record<string, any>, deep: boolean = true, parentKey = "", result: Record<string, any> = {}): Record<string, any> {
		for( const key of Object.keys(obj) ) {
			const newKey = parentKey ? `${parentKey}.${key}` : key;
			if( obj[key] && typeof obj[key] === "object" && !Array.isArray(obj[key]) ) {
				if( deep ) this.flattenObject(obj[key], deep, newKey, result);
			} else {
				result[newKey] = obj[key];
			}
		}
		return result;
	}

	isExporting: boolean = false;
	isExportPreparing: boolean = false;
	totalExportPrepared: number = 0;
	exportInfo: string = "";
	onPhysicalAssessmentsExport() {
		const today: Date = new Date();
		this.exportCSVFromAssessments(`physicalassessments-${this.formatDateFilename(today)}.csv`);
	}
	async exportCSVFromAssessments(filename: string) {
		this.isExporting = true;
		this.isExportPreparing = true;
		const exports: Array<AthleteAssessmentDataModelExport> = this.dataItems.map(a => {
			const exportAssessment = new AthleteAssessmentDataModelExport().load(a);
			this.exportInfo = a.FullName;
			if( this.IsNotEmpty(exportAssessment.athleteLink) ) exportAssessment.athleteLink = `${this.PublicLink}${exportAssessment.athleteLink}`;
			this.totalExportPrepared += 1;
			return exportAssessment;
		});
		this.isExportPreparing = false;

		const headersSet = new Set<string>();
		for( const line of exports ) {
			const keys = Object.keys(line);
			for( const key of keys ) {
				if(this.IsNotEmpty(key)) headersSet.add(key);
			}
		}
		const headers = Array.from(headersSet);
		await this.exportToCSV(filename, exports, headers);
		this.isExporting = false;
	}

	async exportToCSV<T extends Record<string, any>>(filename: string, data: T[], headers: Array<string> = []) {
		if (!data || data.length === 0) {
			console.warn("No data to export");
			return;
		}

		// Extract headers from the keys of the first object
		if( this.IsEmpty(headers) ) headers = Object.keys(data[0]);

		// Escape CSV fields to handle commas, quotes, newlines
		const escapeCSVValue = (value: any) => {
			if (value == null) return "";
			const strValue = String(value);
			if (/[",\n]/.test(strValue)) {
				return `"${strValue.replace(/"/g, '""')}"`;
			}
			return strValue;
		};

		// Build CSV content
		const csvContent = [
			headers.join(","), // header row
			...data.map(row => headers.map(header => escapeCSVValue(row[header])).join(","))
		].join("\n");

		this.downloadUsingBlob(filename, csvContent);
	}
	downloadUsingBlob(filename: string, data: any) {
		const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", filename);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);		
	}

	isSportChanging: boolean = false;
	async onSelectSport() {
		this.isSportChanging = true;
		if( this.IsNotEmpty(this.sport)) this.currentSport = await this.SportRetrieve(this.sport);
		await this.updateTable();
		this.isSportChanging = false;
	}

	editAssessment: AthleteAssessmentDataModelEx;
	isEditingAssessment: boolean = false;
	isEditAssessmentSportAthletesLoaded: boolean = false;
	isEditAssessmentSportAthletesLoading: boolean = false;
	isEditAssessmentAthleteLoaded: boolean = false;
	isEditAssessmentAthleteLoading: boolean = false;
	async onNewAssessmentBegin() {
		this.editAssessment = new AthleteAssessmentDataModelEx();
		this.editAssessment.assessmentDate = new Date();
		this.editAssessment.sport = this.sport;
		await this.onEditAssessmentChangeSport(this.sport);
		if( this.IsNotEmpty(this.athlete) ) await this.onEditAssessmentAthleteChanged(this.athlete);
		this.isEditingAssessment = true;
	}
	get isEditAssessmentNew() {
		return this.IsEmpty(this.editAssessment?.id);
	}
	onEditAssessmentCancel() {
		this.isEditingAssessment = false;
		this.isEditAssessmentAthleteLoaded = false;
		this.editAssessment = undefined;
	}
	async onEditAssessmentConfirm() {
		await athleteAssessmentDataApi.save(this.editAssessment);
		await this.onAthleteSelected(undefined);
		this.isEditingAssessment = false;
		this.isEditAssessmentAthleteLoaded = false;
		this.editAssessment = undefined;
		await this.updateTable();
	}
	async onEditAssessmentChangeSport(sport: SportName) {
		this.isEditAssessmentSportAthletesLoaded = false;
		this.isEditAssessmentSportAthletesLoading = true;
		this.sport = sport;
		await this.onSelectSport();
		this.isEditAssessmentSportAthletesLoading = false;
		this.isEditAssessmentSportAthletesLoaded = true;
	}
	async onEditAssessmentAthleteChanged(athlete: AthleteProfileModel) {
		this.isEditAssessmentAthleteLoaded = false;
		this.isEditAssessmentAthleteLoading = true;
		this.editAssessment.athleteId = athlete?.id;
		this.editAssessment.athlete = await this.AthleteRetrieve(athlete?.id);
		if( this.IsNotEmpty(this.editAssessment.athlete) ) {
			await this.onAthleteSelected(this.editAssessment.athlete);
			if( this.IsEmpty(this.editAssessment.sport) ) {
				this.editAssessment.sport = athlete.Sport.name;
				await this.onEditAssessmentChangeSport(athlete.Sport.name);
			}
			this.editAssessment.playingLevel = this.editAssessment.athlete.AgeGroup;
			this.editAssessment.standingHeight = this.editAssessment.athlete.height;
			this.editAssessment.mass = this.editAssessment.athlete.weight;
			this.editAssessment.gender = this.genderType(this.editAssessment.athlete.gender);
            this.editAssessment.dateOfBirth = this.editAssessment.athlete.BirthDate;
		}
		this.isEditAssessmentAthleteLoading = false;
		this.isEditAssessmentAthleteLoaded = true;
	}
	async onAssessmentEdit(assessment: AthleteAssessmentDataModelEx) {
		this.editAssessment = new AthleteAssessmentDataModelEx().load(assessment);
		if( this.IsEmpty(this.editAssessment.sport) ) {
			this.showAssessmentSportSelect = true;
			return;
		}
		if( this.IsNotEmpty(this.editAssessment.sport) ) await this.onEditAssessmentChangeSport(this.editAssessment.sport);
		await this.onEditAssessmentAthleteChanged(this.editAssessment.athlete);
		this.isEditingAssessment = true;
	}

	showAssessmentSportSelect: boolean = false;
	async onAssessmentSelectSport() {
		const index = this.dataItems.findIndex(i => i.id === this.editAssessment.id);
		if( index >= 0 ) {
			await athleteAssessmentDataApi.patch({id: this.editAssessment.id, sport: this.editAssessment.sport});
			this.dataItems.splice(index, 1, this.editAssessment);
		}
		this.editAssessment = undefined;
		this.showAssessmentSportSelect = false;
	}
	onAssessmentSelectSportCancel() {
		this.editAssessment = undefined;
		this.showAssessmentSportSelect = false;
	}

	isLinking: boolean = false;
	linkAssessmentName: string;
	linkingAssessment: AthleteAssessmentDataModelEx;
	linkingAthlete: AthleteProfileModel;
	athleteSearch: string;
	async onAssessmentLinkAthlete(assessment: AthleteAssessmentDataModelEx) {
		this.linkAssessmentName = assessment.lastName;
		this.linkingAssessment = assessment;
		this.isLinking = true;
	}
	onLinkAthleteSelected( athlete: AthleteProfileModel ){
		this.linkingAthlete = athlete;
	}
	onAssessmentLinkAthleteCancel() {
		this.linkAssessmentName = undefined;
		this.linkingAssessment = undefined;
		this.linkingAthlete = undefined;
		this.isLinking = false;
	}
	async onAssessmentLinkAthleteConfirm() {
		this.linkingAssessment.athleteId = this.linkingAthlete?.id;
		await this.onAssessmentSyncAthlete(this.linkingAssessment);
		this.linkingAthlete = undefined;
		this.isLinking = false;
	}

	isSyncronizing: boolean = false;
	syncAssessmentId: string;
	async onAssessmentSyncAthlete(assessment: AthleteAssessmentDataModelEx) {
		if( this.IsEmpty(assessment?.id) ) return;
		if( this.IsEmpty(assessment?.athleteId) ) return;

		const index = this.dataItems.findIndex(i => i.id === assessment.id);
		if( index < 0 ) return;

		this.syncAssessmentId = assessment.id;
		this.isSyncronizing = true;

		const athlete: AthleteProfileModel = await athleteApi.findById(assessment.athleteId);

		// update assessment
		const updateItem = new AthleteAssessmentDataModelEx().load({
			...assessment,
			id: assessment.id,
			assessmentDate: assessment.assessmentDate? new Date(assessment.assessmentDate) : new Date(),
			sport: assessment.sport?? athlete.Sport?.name,
			teamId: assessment.teamId?? athlete.currentTeam,
			organizationId: assessment.organizationId?? athlete.organizationId,
			playingLevel: assessment.playingLevel?? athlete.AgeGroup,
			gender: assessment.gender?? athlete.gender,
			dominantFoot: assessment.dominantFoot?? athlete.dominantSide,
			playingPosition: assessment.playingPosition?? athlete.Position,
			dateOfBirth: assessment.dateOfBirth?? athlete.BirthDate,
			mass: assessment.mass?? athlete.weight,
			standingHeight: assessment.standingHeight?? athlete.height,
		});

		// update athlete 'data' from all events
		if( this.IsNotEmpty(athlete) ) {
			const purchases: Array<BAPurchaseModel> = await baPurchaseApi.findByAthleteId(athlete.id)
			if( this.IsNotEmpty(purchases) ) {
				const purchasesData: Record<string, any> = purchases.reduce((accumulator, p) => {
					return { ...accumulator, ...(p.data || {}) }
				}, {});
				athlete.data = {...purchasesData, ...athlete.data};
				this.athletesCache.set(athlete);
			}
		}

		// retrieve data from athlete 'data' obtained during registration
		if( this.IsNotEmpty(assessment.athlete?.data) ) {
			if( this.IsEmpty(updateItem.dateOfBirth) ) updateItem.dateOfBirth = assessment.athlete.data.birthDate;
			if( this.IsEmpty(updateItem.gender) ) updateItem.gender = this.genderType(assessment.athlete.data.gender);
			if( this.IsEmpty(updateItem.playingPosition) ) updateItem.playingPosition = assessment.athlete.data.position;
			if( this.IsEmpty(updateItem.mass) ) {
				const weightData = assessment.athlete.data['Weight lbs'];
				const weightInLbs = this.WeightStringToLbs(weightData);
				updateItem.mass = convert(weightInLbs).from(Mass.POUND).to("kg");
			}
			if( this.IsEmpty(updateItem.standingHeight) ) {
				const heightInFt = assessment.athlete.data['Height ft in'];
				const heightInIn = this.HeightStringToInches(heightInFt);
				updateItem.standingHeight = convert(heightInIn).from(Length.INCH).to("cm");
			}
		}
		// save updated item
		const patchedItem = await athleteAssessmentDataApi.update(updateItem);
		const syncItem: AthleteAssessmentDataModelEx = new AthleteAssessmentDataModelEx().load(patchedItem);
		
		syncItem.id = patchedItem.id;

		// update athlete from assessment
		if( this.IsNotEmpty(athlete) ) {
			if( this.IsEmpty(athlete.birthDate) && this.IsNotEmpty(patchedItem.dateOfBirth) ) athlete.birthDate = new Date(patchedItem.dateOfBirth);
			if( this.IsEmpty(athlete.gender) && this.IsNotEmpty(patchedItem.gender) ) athlete.gender = this.athleteGender(patchedItem.gender);
			if( this.IsEmpty(athlete.weight) && this.IsNotEmpty(patchedItem.mass) ) {
				athlete.weight = patchedItem.mass;
				athlete.weightUnit = Mass.KILOGRAM;
			}
			if( this.IsEmpty(athlete.height) && this.IsNotEmpty(patchedItem.standingHeight) ) {
				athlete.height = patchedItem.standingHeight;
				athlete.heightUnit = Length.CENTIMETER;
			}
			const updatedAthlete = await athleteApi.update(athlete);
			this.athletesCache.set(updatedAthlete);
			syncItem.athlete = await this.AthleteRetrieve(updatedAthlete.id);
		}

		syncItem.sportModel = await this.SportRetrieve(assessment.sport);
		this.dataItems.splice(index, 1, syncItem);

		this.syncAssessmentId = undefined;
		this.isSyncronizing = false;
	}

	get AthleteMetrics(): AthleteMetric[]{
		return AthleteMetricValues;
	}
	IsMetricReady(assessment: AthleteAssessmentDataModelEx, metric: AthleteMetric): boolean {
		switch(metric) {
			case AthleteMetric.Acceleration:
				return this.IsNotEmpty(assessment.tenMeterSprint);
			case AthleteMetric.Speed:
				return this.IsNotEmpty(assessment.twentyToThirtyFiveMeterSplit);
			case AthleteMetric.Agility:
				return this.IsNotEmpty(assessment.dropJumpHeight);
			case AthleteMetric.Power:
				return this.IsNotEmpty(assessment.counterMovementJumpHeight);
			case AthleteMetric.Recovery:
				return this.IsNotEmpty(assessment.yoyoIntermittentRecoveryTestStage);
		}
		return true;
	}

	PhysicalAssessmentLabels(item: AthleteAssessmentDataModelEx): Array<SportLabel> {
		return item.sportModel.physicalAssessmentLabels;
	}
}
